import type { Session, Subagent, WorkflowRun } from '@orgus/protocol';
import { RUN_STATUS_LABEL } from '@orgus/protocol';
import { Bot, SquareTerminal, Workflow } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button, GROUP_INDENT, Tooltip, cn, formatClock, formatDuration, plural, useNow } from '@orgus/ui';
import { isLive } from './labels.ts';
import { plainText } from '../../lib/plainText.ts';

/** A lane's colour: only running (working), needs you (attention) and failed (danger) carry one. */
type LaneTone = 'working' | 'attention' | 'danger' | 'neutral';

const BAR: Record<LaneTone, { bar: string; fill: string; edge: string }> = {
  working: { bar: 'border-status-working-line bg-status-working-soft', fill: 'bg-status-working', edge: 'bg-status-working' },
  attention: { bar: 'border-status-attention-line bg-status-attention-soft', fill: 'bg-status-attention', edge: 'bg-status-attention' },
  danger: { bar: 'border-status-danger-line bg-status-danger-soft', fill: 'bg-status-danger', edge: 'bg-status-danger' },
  neutral: { bar: 'border-border bg-hover', fill: 'bg-fg-faint', edge: 'bg-fg-subtle' },
};

function runLaneTone(status: Subagent['status']): LaneTone {
  return status === 'running' ? 'working' : status === 'failed' ? 'danger' : 'neutral';
}

interface Lane {
  key: string;
  kind: 'session' | 'subagent' | 'workflow';
  label: string;
  start: number;
  end: number | null;
  tone: LaneTone;
  tip: ReactNode;
  /** Workflow phases: done fraction per phase, drawn inside the bar. */
  phases?: WorkflowRun['phases'];
}

const MAX_LANES = 8;

function subLane(a: Subagent): Lane {
  return {
    key: a.id,
    kind: 'subagent',
    label: a.description || a.type,
    start: a.startedAt,
    end: a.endedAt,
    tone: runLaneTone(a.status),
    tip: (
      <span className="flex max-w-72 flex-col gap-1">
        <span className="font-medium">{a.description || a.type}</span>
        <span className="text-fg-muted">
          {a.type}, {RUN_STATUS_LABEL[a.status].toLowerCase()}, {plural(a.toolCalls, 'tool call')}
        </span>
        {a.lastText && <span className="line-clamp-3 text-fg-muted">{plainText(a.lastText)}</span>}
      </span>
    ),
  };
}

function flowLane(w: WorkflowRun): Lane {
  const done = w.phases.filter((p) => p.done >= p.agents).length;
  return {
    key: w.id,
    kind: 'workflow',
    label: w.name,
    start: w.startedAt,
    end: w.endedAt,
    tone: runLaneTone(w.status),
    phases: w.phases,
    tip: (
      <span className="flex max-w-72 flex-col gap-1">
        <span className="font-medium">{w.name}</span>
        {w.description && <span className="line-clamp-3 text-fg-muted">{w.description}</span>}
        <span className="text-fg-muted">
          {RUN_STATUS_LABEL[w.status]}, {done} of {plural(w.phases.length, 'phase')} done
        </span>
        <span className="flex flex-col">
          {w.phases.map((p) => (
            <span key={p.title} className="flex justify-between gap-4 text-fg-muted num">
              <span>{p.title}</span>
              <span>
                {p.done}/{p.agents}
              </span>
            </span>
          ))}
        </span>
      </span>
    ),
  };
}

const KIND_ICON = { session: SquareTerminal, subagent: Bot, workflow: Workflow };

/** A small Gantt of the session and everything it ran: subagents and workflows as lanes, live bars growing. */
export function SessionLanes({ session }: { session: Session }) {
  const live = isLive(session);
  const now = useNow(live ? 1000 : 60_000);
  const [all, setAll] = useState(false);

  const children: Lane[] = [...session.subagents.map(subLane), ...session.workflows.map(flowLane)].sort((a, b) => a.start - b.start);
  const ends = [session.lastActivity, ...children.map((l) => l.end ?? 0)];
  const tEnd = Math.max(session.startedAt + 60_000, live ? now : Math.max(...ends));
  // Zoom to the children: a session open for days would draw its subagents as slivers. When the
  // session has gone quiet since they ended, the window ends with them and the main thread runs on
  // past its right edge.
  const first = children.length ? Math.min(...children.map((l) => l.start)) : session.startedAt;
  const lastEnd = children.length ? Math.max(...children.map((l) => l.end ?? tEnd)) : tEnd;
  const pad = Math.max(60_000, (lastEnd - first) * 0.08);
  const t0 = Math.max(session.startedAt, first - pad);
  const t1 = Math.max(t0 + 60_000, Math.min(tEnd, lastEnd + pad));
  const atNow = t1 >= tEnd;
  const zoomed = t0 > session.startedAt;
  const span = t1 - t0;
  const pos = (t: number) => Math.min(100, Math.max(0, ((t - t0) / span) * 100));

  const main: Lane = {
    key: session.id,
    kind: 'session',
    label: 'Main thread',
    start: t0,
    end: live ? null : session.lastActivity,
    tone: session.status === 'waiting' ? 'attention' : session.status === 'working' ? 'working' : 'neutral',
    tip: `${plural(session.turns, 'turn')}, ${plural(session.toolCalls, 'tool call')}`,
  };
  const shown = all ? children : children.slice(0, MAX_LANES);
  const lanes = [main, ...shown];

  return (
    <div className="flex flex-col">
      <div className="flex h-lane items-center gap-3 border-b border-border-subtle text-caption text-fg-subtle num">
        <span className="w-44 shrink-0">
          {zoomed ? 'From' : 'Started'} {formatClock(t0)}
        </span>
        <span className="flex flex-1 justify-between">
          <span>{zoomed || !atNow ? 'Zoomed to its subagents and workflows' : `${formatDuration(span)} in all`}</span>
          <span>{live && atNow ? 'Now' : formatClock(t1)}</span>
        </span>
      </div>
      <ul className="flex flex-col">
        {lanes.map((l) => {
          const Icon = KIND_ICON[l.kind];
          const left = pos(l.start);
          const right = pos(l.end ?? now);
          const width = Math.max(0.8, right - left);
          const running = l.end == null;
          const c = BAR[l.tone];
          return (
            <li key={l.key} className="flex h-lane items-center gap-3">
              {/* Subagents and workflows sit one group step in: their icon under the main thread's label. */}
              <span className={cn('flex w-44 shrink-0 items-center gap-2 text-ui', l.kind === 'session' ? 'text-fg' : cn(GROUP_INDENT, 'text-fg-muted'))}>
                <Icon className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                <span className="truncate">{l.label}</span>
              </span>
              <span className="relative h-full flex-1">
                <span aria-hidden className="absolute inset-x-0 top-1/2 h-px bg-border-subtle" />
                <Tooltip content={l.tip}>
                  <span
                    tabIndex={0}
                    role="img"
                    aria-label={`${l.label}: ${running ? 'running' : 'finished'}, ${formatDuration((l.end ?? now) - l.start)}`}
                    className={cn('absolute top-1/2 flex h-3 -translate-y-1/2 overflow-hidden rounded-control border transition-all duration-(--duration-slow)', c.bar)}
                    style={{ left: `${left}%`, width: `${width}%` }}
                  >
                    {l.phases && (
                      <span className="flex h-full w-full gap-px">
                        {l.phases.map((p) => (
                          <span key={p.title} className="relative h-full flex-1 overflow-hidden">
                            <span className={cn('absolute inset-y-0 left-0 opacity-50', c.fill)} style={{ width: `${p.agents ? (p.done / p.agents) * 100 : 0}%` }} />
                          </span>
                        ))}
                      </span>
                    )}
                    {running && <span className={cn('absolute right-0 top-0 h-full w-0.5', c.edge)} aria-hidden />}
                  </span>
                </Tooltip>
              </span>
            </li>
          );
        })}
      </ul>
      {children.length > MAX_LANES && (
        <div className="flex gap-3">
          <span className="w-44 shrink-0" aria-hidden />
          <Button variant="ghost" size="sm" onClick={() => setAll((v) => !v)}>
            {all ? 'Show fewer' : `Show ${children.length - MAX_LANES} more`}
          </Button>
        </div>
      )}
    </div>
  );
}
